import React, { useState, useEffect } from 'react';
import { HashRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import FormWizard from './components/FormWizard';
import SubmissionsList from './components/SubmissionsList';
import { AdminLoginPage } from './pages/AdminLoginPage';
import { AdminSubmissionsPage } from './pages/AdminSubmissionsPage';
import { RetrievePage } from './pages/RetrievePage';
import { ReferralPage } from './pages/ReferralPage';
import { AdminReferralsPage } from './pages/AdminReferralsPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { StaffLoginPage } from './pages/StaffLoginPage';
import { StaffSignupPage } from './pages/StaffSignupPage';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { LanguageSelectionModal } from './components/LanguageSelectionModal';
import { ViewStateProvider, useViewState } from './contexts/ViewStateContext';
import { getActiveDashboardSession, clearDashboardSessions, resumeSessionExpiryTimer } from './utils/session';

const LanguageToggle: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { language, setLanguage } = useLanguage();
  
  const handleLanguageChange = (newLanguage: 'en' | 'es') => {
    if (newLanguage === language) return;
    
    const message = newLanguage === 'es' 
      ? 'Your PDF will be generated in Spanish. Do you want to continue?'
      : 'Your PDF will be generated in English. Do you want to continue?';
    
    const confirmed = window.confirm(message);
    if (confirmed) {
      setLanguage(newLanguage);
    }
  };
  
  return (
    <div
      className={`inline-flex items-center rounded-md border border-gray-300 overflow-hidden text-xs font-semibold ${className}`}
      role="group"
      aria-label="Language selector"
    >
      <button
        type="button"
        onClick={() => handleLanguageChange('en')}
        className={`px-3 py-1.5 transition ${
          language === 'en'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        aria-pressed={language === 'en'}
      >
        EN
      </button>
      <button
        type="button"
        onClick={() => handleLanguageChange('es')}
        className={`px-3 py-1.5 transition border-l border-gray-300 ${
          language === 'es'
            ? 'bg-blue-600 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50'
        }`}
        aria-pressed={language === 'es'}
      >
        ES
      </button>
    </div>
  );
};

const UserAvatarMenu: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const session = getActiveDashboardSession();
  const navigate = useNavigate();

  if (!session) return null;

  const user = session.user;
  const initial = user.firstName ? user.firstName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase();
  const fullName = `${user.firstName} ${user.lastName}`.trim() || user.email;

  const handleLogout = () => {
    clearDashboardSessions();
    navigate('/staff/login');
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white font-bold hover:bg-blue-700 transition"
      >
        {initial}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
          <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-50 overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-100 text-sm text-gray-700 font-medium bg-gray-50">
              {fullName}
            </div>
            <button
              onClick={() => {
                navigate('/admin/submissions');
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 border-b border-gray-100"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
              Dashboard
            </button>
            <button
              onClick={handleLogout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
              Logout
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const AppLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, setLanguage } = useLanguage();
  const { viewState } = useViewState();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  
  const hideGlobalNav =
    location.pathname.startsWith('/admin/') || location.pathname.startsWith('/staff/');

  const searchParams = new URLSearchParams(location.search);
  const isEditingContext = !!searchParams.get('editSubmissionId') || !!searchParams.get('draftId');

  // Resume the client-side session-expiry timer on app load, so a page
  // reload mid-session still logs out at the token's real expiry rather
  // than losing track of the countdown.
  useEffect(() => {
    resumeSessionExpiryTimer();
  }, []);

  // Check for new session on component mount
  useEffect(() => {
    const hasSelectedLanguage = localStorage.getItem('css_forms_language');
    if (!hasSelectedLanguage) {
      setShowLanguageModal(true);
    }
  }, []);

  // Handle new form click
  const handleNewFormClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setPendingNavigation('/submit-forms');
    setShowLanguageModal(true);
    setMobileMenuOpen(false);
  };

  // Handle language selection from modal
  const handleLanguageSelect = (language: 'en' | 'es') => {
    setLanguage(language);
    setShowLanguageModal(false);
    
    if (pendingNavigation) {
      // For new forms, add a query parameter to force reset
      const url = pendingNavigation === '/submit-forms' ? '/submit-forms?new=true' : pendingNavigation;
      navigate(url);
      setPendingNavigation(null);
    }
  };

  // Handle modal close (only if it's not a new session)
  const handleModalClose = () => {
    const hasSelectedLanguage = localStorage.getItem('css_forms_language');
    if (hasSelectedLanguage) {
      setShowLanguageModal(false);
      setPendingNavigation(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {!hideGlobalNav && (
        <nav className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img src="/css_logo.png" alt="California Support Services" className="h-10 sm:h-12" />
                </Link>
              </div>

              <div className="hidden md:flex items-center gap-4">
                {viewState !== 'preview' && !isEditingContext && (
                  <button
                    onClick={handleNewFormClick}
                    className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                  >
                    {t('nav.newForm')}
                  </button>
                )}
                {viewState !== 'preview' && <LanguageToggle />}
                <UserAvatarMenu />
              </div>

              <div className="md:hidden flex items-center gap-2">
                {viewState !== 'preview' && <LanguageToggle />}
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    {mobileMenuOpen ? (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    ) : (
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    )}
                  </svg>
                </button>
                <div className="ml-1">
                  <UserAvatarMenu />
                </div>
              </div>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {viewState !== 'preview' && !isEditingContext && (
                  <button
                    onClick={handleNewFormClick}
                    className="block w-full text-left text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-md text-base font-medium"
                  >
                    {t('nav.newForm')}
                  </button>
                )}
              </div>
            </div>
          )}
        </nav>
      )}

      <main>
        <Routes>
          <Route path="/" element={<Navigate to="/submit-forms" replace />} />
          <Route path="/submit-forms" element={<FormWizard />} />
          <Route path="/submissions" element={<SubmissionsList />} />
          <Route path="/retrieve" element={<RetrievePage />} />
          <Route path="/staff/login" element={<StaffLoginPage />} />
          <Route path="/staff/signup" element={<StaffSignupPage />} />
          <Route path="/admin/login" element={<AdminLoginPage />} />
          <Route path="/admin/submissions" element={<AdminSubmissionsPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/admin/referrals" element={<AdminReferralsPage />} />
        </Routes>
      </main>

      {/* Language Selection Modal */}
      <LanguageSelectionModal 
        isOpen={showLanguageModal}
        onLanguageSelect={handleLanguageSelect}
        onClose={handleModalClose}
      />
    </div>
  );
};

function App() {
  return (
    <LanguageProvider>
      <ViewStateProvider>
        <Router>
          <AppLayout />
        </Router>
      </ViewStateProvider>
    </LanguageProvider>
  );
}

export default App;
